/**
 * BarChart.tsx — recharts horizontal-or-vertical bar wrapper.
 *
 * One bar series per call. For multi-series stacked bars use recharts
 * directly inside a page — this helper is intentionally minimal.
 */

import React from 'react';
import {
	Bar,
	BarChart as RBarChart,
	CartesianGrid,
	Cell,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from 'recharts';

import EmptyState from './EmptyState';
import { formatCrore } from '../helpers/format';

export interface BarDatum {
	name: string;
	value: number;
	color?: string;
}

interface Props {
	data: BarDatum[];
	height?: number;
	/** Default fill when `BarDatum.color` is absent. */
	fill?: string;
	valueFormatter?: (v: number) => string;
	/** Make Y axis read in ₹Cr-friendly compact form. */
	yAxisLabel?: string;
	xAxisAngle?: number;
	layout?: 'horizontal' | 'vertical';
}

export default function BarChart({
	data,
	height = 280,
	fill = 'var(--navy-500)',
	valueFormatter = (v) => formatCrore(v, 1),
	yAxisLabel,
	xAxisAngle = -25,
	layout = 'horizontal',
}: Props) {
	const safe = Array.isArray(data) ? data : [];
	if (!safe.length) {
		return <EmptyState title="No data" hint="Nothing to chart." />;
	}

	const isVertical = layout === 'vertical';

	return (
		<div style={{ width: '100%', height }}>
			<ResponsiveContainer width="100%" height="100%">
				<RBarChart
					data={safe}
					layout={layout}
					margin={{ top: 12, right: 16, bottom: isVertical ? 8 : 50, left: isVertical ? 96 : 8 }}
				>
					<CartesianGrid strokeDasharray="3 3" stroke="var(--ink-100)" />
					{isVertical ? (
						<>
							<XAxis
								type="number"
								tick={{ fontSize: 11, fill: 'var(--text-dim)' }}
								tickFormatter={(v: number) => valueFormatter(v).replace('₹ ', '').replace(' cr', '')}
								label={
									yAxisLabel
										? { value: yAxisLabel, position: 'insideBottom', offset: -4, fontSize: 11 }
										: undefined
								}
							/>
							<YAxis
								dataKey="name"
								type="category"
								width={120}
								tick={{ fontSize: 11, fill: 'var(--text-dim)' }}
							/>
						</>
					) : (
						<>
							<XAxis
								dataKey="name"
								tick={{ fontSize: 11, fill: 'var(--text-dim)' }}
								angle={xAxisAngle}
								height={70}
								interval={0}
								textAnchor="end"
							/>
							<YAxis
								tick={{ fontSize: 11, fill: 'var(--text-dim)' }}
								tickFormatter={(v: number) => valueFormatter(v).replace('₹ ', '').replace(' cr', '')}
								label={
									yAxisLabel
										? { value: yAxisLabel, angle: -90, position: 'insideLeft', fontSize: 11 }
										: undefined
								}
							/>
						</>
					)}
					<Tooltip formatter={(v: number | string) => (typeof v === 'number' ? valueFormatter(v) : String(v))} />
					<Bar dataKey="value" radius={[4, 4, 0, 0]}>
						{safe.map((d, i) => (
							<Cell key={`${d.name}-${i}`} fill={d.color || fill} />
						))}
					</Bar>
				</RBarChart>
			</ResponsiveContainer>
		</div>
	);
}
